import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { getUsers, updateUserStatus } from '../../../lib/api';
import './admin.css';

const PAGE_SIZE = 10;

const formatMoney = (v) => {
  const n = Number(v);
  if (Number.isNaN(n) || v === '' || v == null) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
};

const ManageUsers = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadUsers = async (nextPage) => {
    setError('');
    setLoading(true);
    try {
      const data = await getUsers(nextPage, PAGE_SIZE);
      setRows(data?.data || []);
      setTotalPages(data?.pagination?.total_pages || 1);
      setPage(nextPage);
    } catch (e) {
      setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1);
  }, []);

  const onToggle = async (userId, checked) => {
    const status = checked ? 1 : 0;
    setRows((prev) => prev.map((u) => (u.id === userId ? { ...u, status } : u)));
    try {
      await updateUserStatus(userId, status);
    } catch (e) {
      setError(e?.message || 'Failed to update status');
      setRows((prev) => prev.map((u) => (u.id === userId ? { ...u, status: checked ? 0 : 1 } : u)));
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div>
            <span className="admin-page__eyebrow">Administration</span>
            <h1 className="admin-page__title">Manage users</h1>
            <p className="admin-page__subtitle">
              Review accounts, balances from the user directory API, IDs, and activation. Use the toggle to enable or disable a user.
            </p>
          </div>
        </div>
      </header>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>User directory</h2>
          <p>{loading ? 'Loading…' : `${rows.length} user(s) on this page`}</p>
        </div>
        <div className="admin-panel__body" style={{ padding: 0 }}>
          {error && (
            <div className="admin-feedback admin-feedback--error" style={{ margin: '16px 20px 0' }}>
              {error}
            </div>
          )}
          <div className="admin-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone number</th>
                  <th>Balance</th>
                  <th>Remaining</th>
                  <th>WABA ID</th>
                  <th>Phone ID</th>
                  <th style={{ width: 100 }}>Active</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="admin-empty">Loading users…</td>
                  </tr>
                ) : (
                  <>
                    {rows.map((u) => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>{u.name || '—'}</td>
                        <td>{u.email || '—'}</td>
                        <td>{u.phone_number || u.phoneNumber || '—'}</td>
                        <td>{formatMoney(u.balance)}</td>
                        <td>{formatMoney(u.remaining_balance ?? u.balance)}</td>
                        <td>{u.waba_id || '—'}</td>
                        <td>{u.phone_number_id || '—'}</td>
                        <td>
                          <label className="admin-switch" title={u.status === 1 ? 'Active' : 'Inactive'}>
                            <input
                              type="checkbox"
                              checked={u.status === 1}
                              onChange={(e) => onToggle(u.id, e.target.checked)}
                            />
                            <span className="admin-switch-slider" />
                          </label>
                        </td>
                      </tr>
                    ))}
                    {!rows.length && (
                      <tr>
                        <td colSpan="9" className="admin-empty">No users found.</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className="admin-pagination">
            <span className="admin-pagination__meta">Page {page} of {totalPages}</span>
            <div className="admin-pagination__controls">
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                onClick={() => loadUsers(Math.max(page - 1, 1))}
                disabled={page <= 1 || loading}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Previous
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                onClick={() => loadUsers(Math.min(page + 1, totalPages))}
                disabled={page >= totalPages || loading}
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManageUsers;
